/**
 * Agrupa puntos (x,y) en coordenadas de imagen dentro de un radio en píxeles.
 * mergeMode: count | sum | avg sobre el peso de cada punto (1 si no hay columna de peso).
 */
export function clusterPoints(
  points,
  { enabled = false, radiusPx = 25, mergeMode = 'count' } = {}
) {
  if (!enabled || !points?.length) {
    return (points || []).map((p) => ({
      ...p,
      count: 1,
      members: [p],
    }));
  }

  const radius = Number(radiusPx);
  if (!Number.isFinite(radius) || radius <= 0) return points;

  const r2 = radius * radius;
  const cellSize = radius;
  const parent = points.map((_, i) => i);

  const find = (i) => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    let cur = i;
    while (parent[cur] !== cur) {
      const next = parent[cur];
      parent[cur] = root;
      cur = next;
    }
    return root;
  };

  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  const grid = new Map();
  const cellKey = (x, y) => `${Math.floor(x / cellSize)},${Math.floor(y / cellSize)}`;

  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const cx = Math.floor(p.x / cellSize);
    const cy = Math.floor(p.y / cellSize);

    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        const bucket = grid.get(`${cx + dx},${cy + dy}`);
        if (!bucket) continue;
        for (const j of bucket) {
          const q = points[j];
          if (p.group !== q.group) continue;
          const ddx = p.x - q.x;
          const ddy = p.y - q.y;
          if (ddx * ddx + ddy * ddy <= r2) union(i, j);
        }
      }
    }

    const key = cellKey(p.x, p.y);
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(i);
  }

  const buckets = new Map();
  for (let i = 0; i < points.length; i += 1) {
    const root = find(i);
    if (!buckets.has(root)) buckets.set(root, []);
    buckets.get(root).push(i);
  }

  const mode = mergeMode === 'sum' || mergeMode === 'avg' ? mergeMode : 'count';
  const out = [];

  for (const indices of buckets.values()) {
    let sumX = 0;
    let sumY = 0;
    let sumWeight = 0;
    let count = 0;
    let group = '';

    for (const idx of indices) {
      const p = points[idx];
      sumX += p.x;
      sumY += p.y;
      sumWeight += p.weight != null && Number.isFinite(p.weight) ? p.weight : 1;
      count += 1;
      if (!group && p.group) group = p.group;
    }

    let weight;
    if (mode === 'sum') {
      weight = sumWeight;
    } else if (mode === 'avg') {
      weight = count > 0 ? sumWeight / count : 1;
    } else {
      weight = count;
    }

    const members = indices.map((idx) => points[idx]);
    out.push({
      x: sumX / count,
      y: sumY / count,
      weight,
      group,
      count,
      members,
    });
  }

  return out;
}
